/**
 * KAN-848: проверка авторизации сессии Telegram-аккаунта (POST {id}/auth-check/).
 * Кнопка есть у каждого аккаунта держателя (любой статус); свежая сессия (моложе суток)
 * не проверяется (KAN-661). Массовая проверка идёт по одному аккаунту с паузой.
 * Чистые функции над строкой таблицы.
 */
import { describeApiError } from '@/lib/telegramConnect';
import { isFreshSession } from '@/lib/telegramSessionAge';
import type { AuthCheckResult, TelegramAccount } from '@/types/telegramAccount';

export const AUTH_CHECK_LABEL = 'Проверить авторизацию';
export const AUTH_CHECK_PENDING_LABEL = 'Проверяем авторизацию...';
export const BULK_AUTH_CHECK_LABEL = 'Проверить все';
export const BULK_AUTH_CHECK_STOP_LABEL = 'Остановить';
/** Пауза между аккаунтами в массовой проверке, мс. */
export const BULK_AUTH_PAUSE_MS = 3000;
export const AUTH_CHECK_UNAVAILABLE_MESSAGE = 'Держатель сессий недоступен';
export const AUTH_CHECK_TIMEOUT_MESSAGE = 'Держатель не ответил вовремя, повторите позже';
export const AUTH_CHECK_FALLBACK_MESSAGE = 'Не удалось проверить авторизацию';

type CheckableAccount = Pick<
  TelegramAccount,
  'holder' | 'account_id' | 'decommissioned'
>;

/** Кнопка проверки авторизации: аккаунт держателя gateway, не основной и не списанный. */
export function canCheckAuth(account: CheckableAccount): boolean {
  return (
    account.holder === 'gateway' &&
    account.account_id !== 'primary' &&
    !account.decommissioned
  );
}

export type AuthToastKind = 'success' | 'error' | 'info';

/** Вид уведомления по результату: есть авторизация, нет, не определено. */
export function authResultToast(result: AuthCheckResult): { kind: AuthToastKind; text: string } {
  const kind: AuthToastKind =
    result.authorized === true ? 'success' : result.authorized === false ? 'error' : 'info';
  return { kind, text: result.text || AUTH_CHECK_FALLBACK_MESSAGE };
}

interface ErrLike {
  response?: { status?: number };
}

/** Текст ошибки запроса проверки авторизации. */
export function authCheckErrorMessage(err: unknown): string {
  const status = (err as ErrLike | null)?.response?.status;
  if (status === 503) return AUTH_CHECK_UNAVAILABLE_MESSAGE;
  if (status === 504) return AUTH_CHECK_TIMEOUT_MESSAGE;
  return describeApiError(err, AUTH_CHECK_FALLBACK_MESSAGE);
}

export interface BulkPlan {
  targets: TelegramAccount[];
  /** Пропущены: сессия моложе суток. */
  skippedFresh: number;
}

/** Кого проверять массово: проверяемые аккаунты без свежих сессий. */
export function bulkAuthPlan(
  accounts: ReadonlyArray<TelegramAccount>,
  now: Date = new Date()
): BulkPlan {
  const checkable = accounts.filter(canCheckAuth);
  const targets = checkable.filter((a) => !isFreshSession(a, now));
  return { targets, skippedFresh: checkable.length - targets.length };
}

export interface BulkTally {
  authorized: number;
  notAuthorized: number;
  unknown: number;
  failed: number;
}

export const EMPTY_TALLY: BulkTally = { authorized: 0, notAuthorized: 0, unknown: 0, failed: 0 };

/** Счётчик массовой проверки после ответа по одному аккаунту (null: запрос упал). */
export function addToTally(tally: BulkTally, result: AuthCheckResult | null): BulkTally {
  if (result === null) return { ...tally, failed: tally.failed + 1 };
  if (result.authorized === true) return { ...tally, authorized: tally.authorized + 1 };
  if (result.authorized === false) return { ...tally, notAuthorized: tally.notAuthorized + 1 };
  return { ...tally, unknown: tally.unknown + 1 };
}

/** Итог массовой проверки одной строкой. */
export function bulkSummary(tally: BulkTally, skippedFresh: number): string {
  const parts = [
    `авторизованы: ${tally.authorized}`,
    `нет авторизации: ${tally.notAuthorized}`,
  ];
  if (tally.unknown) parts.push(`не определено: ${tally.unknown}`);
  if (tally.failed) parts.push(`ошибка запроса: ${tally.failed}`);
  if (skippedFresh) parts.push(`пропущены свежие: ${skippedFresh}`);
  return `Проверка завершена (${parts.join(', ')})`;
}
